import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { PublicStats } from "@/components/PublicStats";
import { LegalFooter } from "@/components/hud/LegalFooter";
import { HeroDisplay } from "@/components/landing/HeroDisplay";
import { HomeHeroBackdrop } from "@/components/landing/HomeHeroBackdrop";
import { ShowcaseVideos } from "@/components/landing/ShowcaseVideos";
import { useAuth } from "@/lib/auth";
import { LANDING } from "@/constants/testIds";

const fade = (delay: number) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] as const },
});

export default function Landing() {
  const { user } = useAuth();
  const { t } = useTranslation();
  // Long single words (e.g. Greek/German/Irish headlines) get a smaller type scale so they never break mid-word.
  const longWord = ["landing.h1a", "landing.h1b", "landing.h1c", "landing.h1d"].some((k) => t(k).split(/\s+/).some((w) => w.length > 12));
  return (
    <Layout backdrop="full">
      <div className="relative">
      <HomeHeroBackdrop />
        <main data-testid={LANDING.page} className="relative mx-auto max-w-7xl px-6 pb-44 pt-16 sm:px-10 lg:pt-24 xl:pt-28">
          <section className="relative grid items-center gap-16 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14">
            <div className="relative">
              <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-[420px] w-[420px] rounded-full bg-[#2E7BFF]/20 blur-[140px]" />
              <motion.span
                {...fade(0)}
                className="relative inline-flex items-center gap-3 font-display text-[11px] font-semibold uppercase tracking-[0.36em] text-[#9CC8FF]"
              >
                <span className="h-px w-10 bg-gradient-to-r from-[#7FD4FF] to-transparent shadow-[0_0_10px_#2E7BFF]" />
                {t("landing.eyebrow")}
              </motion.span>

              <motion.h1
                {...fade(0.08)}
                className="headline-shadow relative mt-7 font-display font-bold uppercase leading-[0.92] tracking-[0.02em] text-foreground max-sm:[overflow-wrap:anywhere]"
              >
                <span className={cn("block", longWord ? "text-[2.1rem] sm:text-5xl lg:text-[2.9rem] xl:text-[3.3rem]" : "text-[2.7rem] sm:text-6xl lg:text-[3.6rem] xl:text-[4rem]")}>{t("landing.h1a")}</span>
                <span className={cn("block", longWord ? "text-[2.1rem] sm:text-5xl lg:text-[2.9rem] xl:text-[3.3rem]" : "text-[2.7rem] sm:text-6xl lg:text-[3.6rem] xl:text-[4rem]")}>{t("landing.h1b")}</span>
                <span className={cn("text-neon-strong mt-2 block", longWord ? "text-[2rem] sm:text-5xl lg:text-[3.3rem] xl:text-[3.8rem]" : "text-[2.55rem] sm:text-7xl lg:text-[4.2rem] xl:text-[4.7rem]")}>{t("landing.h1c")}</span>
                <span className={cn("text-neon-strong block", longWord ? "text-[2rem] sm:text-5xl lg:text-[3.3rem] xl:text-[3.8rem]" : "text-[2.55rem] sm:text-7xl lg:text-[4.2rem] xl:text-[4.7rem]")}>{t("landing.h1d")}</span>
              </motion.h1>

              <motion.div {...fade(0.16)} className="relative mt-7 flex items-center gap-4">
                <span aria-hidden className="h-8 w-px bg-gradient-to-b from-[#7FD4FF] to-transparent" />
                <p className="max-w-sm text-base leading-relaxed text-[#B8C4DA] sm:text-lg">
                  {t("landing.subtitle")}
                </p>
              </motion.div>

              <motion.div {...fade(0.24)} className="relative mt-12 flex flex-wrap items-center gap-6">
                <Link to={user ? "/create" : "/login"} data-testid={LANDING.ctaStyleGuide} className="cta-hero group text-base">
                  <span className="inline-flex items-center gap-3">
                    {user ? t("landing.ctaUser") : t("landing.ctaGuest")}
                    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
                <span className="hidden items-center gap-2 font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-[#7FD4FF]/70 sm:inline-flex">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#7FD4FF] shadow-[0_0_10px_#7FD4FF]" />
                  {t("landing.flow")}
                </span>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className="relative pb-24 lg:pb-28"
            >
              <HeroDisplay />
            </motion.div>
          </section>
        </main>
      </div>
      <ShowcaseVideos />
      <LegalFooter />
      <PublicStats />
    </Layout>
  );
}
